import {
  ColumnDirective,
  ColumnsDirective,
  Edit,
  GridComponent,
  Inject,
  Page,
  Toolbar,
  ColumnChooser
} from '@syncfusion/ej2-react-grids'
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import './NewMain.css'

import { BASE_URL, deleteCall, get } from '../../Services/Calls'
import { Header, Pagination } from '../../components'
import CreateAddIcon from '../../data/createAddIcon.png'
import { selfGuidedGrid } from '../../data/dummy'
import { setGridVisibility, getGridVisibility } from '../../Services/gridVisibilityHelper'

const PAGE_SIZE = 10

const SelfGuided = () => {
  const navigate = useNavigate()
  const toolbarOptions = ['Delete', 'Search', 'ColumnChooser']

  const [selectedRecords, setSelectedRecords] = useState([])
  const [selfGuidedData, setSelfGuidedData] = useState([])
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  const editOptions = {
    allowEditing: false,
    allowAdding: true,
    allowDeleting: true
  }

  const pageKey = 'SGP-page'
  const [isReady, setIsReady] = useState(false)
  const [columnSettings, setColumnSettings] = useState([])

  useEffect(() => {
    const saved = getGridVisibility(pageKey)
    if (saved) setColumnSettings(saved)
    setIsReady(true)
  }, [])

  const finalColumns = selfGuidedGrid(setLoading).map((col) => {
    if (!col.field) return col
    const match = columnSettings?.find((c) => c.field === col.field)
    return match ? { ...col, visible: match.visible } : { ...col }
  })

  useEffect(() => {
    getSelfGuided(1, '')
  }, [])

  const getSelfGuided = async (page, search) => {
    setLoading(true)
    try {
      const countryId = localStorage.getItem('selectedCountry')
      const trimmedSearch = search ? search.trim() : ''
      const isIdSearch = trimmedSearch && /^\d+$/.test(trimmedSearch)
      const skip = (page - 1) * PAGE_SIZE

      if (isIdSearch) {
        // The grid's ID column shows the friendly_id, not the record's UUID
        // primary key the detail route expects, so match it against the full list.
        const response = await get(`${BASE_URL}/self-guided-packages?order=DESC&take=999`)
        const items = Array.isArray(response) ? response : response?.results ?? []
        const match = items.find((item) => String(item.friendly_id) === trimmedSearch)
        const matchesCountry = match && (!countryId || String(match.trip?.country?.id) === String(countryId))
        if (matchesCountry) {
          setSelfGuidedData([match])
          setTotalPages(1)
          setTotalItems(1)
        } else {
          setSelfGuidedData([])
          setTotalPages(1)
          setTotalItems(0)
        }
        return
      }

      let url = `${BASE_URL}/self-guided-packages?order=DESC`
      if (search) url += `&name=${encodeURIComponent(search)}`

      if (countryId) {
        // No backend endpoint filters by country, so fetch the matching
        // records and filter/paginate client-side (same approach used
        // elsewhere in this admin, e.g. Trips.jsx).
        const response = await get(`${url}&take=999`)
        const items = Array.isArray(response) ? response : response?.results ?? []
        const filtered = items.filter((item) => String(item.trip?.country?.id) === String(countryId))
        const total = filtered.length
        setSelfGuidedData(filtered.slice(skip, skip + PAGE_SIZE))
        setTotalPages(Math.ceil(total / PAGE_SIZE) || 1)
        setTotalItems(total)
        return
      }

      url += `&take=${PAGE_SIZE}&skip=${skip}`
      const response = await get(url)
      if (response) {
        if (Array.isArray(response)) {
          const total = response.length
          setSelfGuidedData(response.slice(skip, skip + PAGE_SIZE))
          setTotalPages(Math.ceil(total / PAGE_SIZE) || 1)
          setTotalItems(total)
        } else {
          const items = response.results ?? []
          const total = response.count ?? items.length
          setSelfGuidedData(items)
          setTotalPages(Math.ceil(total / PAGE_SIZE) || 1)
          setTotalItems(total)
        }
      }
    } catch (ex) {
      alert(ex)
    } finally {
      setLoading(false)
    }
  }

  const onPageChange = (page) => {
    setCurrentPage(page)
    setSelectedRecords([])
    getSelfGuided(page, searchTerm)
  }

  const toolbarClick = (args) => {
    if (args.item.text === 'Delete') {
      args.cancel = true
      setDeleteConfirm(true)
    }
  }

  const actionBegin = (args) => {
    if (args.requestType === 'searching') {
      args.cancel = true
      const term = args.searchString || ''
      setSearchTerm(term)
      setCurrentPage(1)
      setSelectedRecords([])
      getSelfGuided(1, term)
    }
  }

  const handleDelete = async () => {
    setLoading(true)
    setDeleteConfirm(false)
    try {
      for (let i = 0; i < selectedRecords.length; i++) {
        await deleteCall(BASE_URL + '/self-guided-packages/' + selectedRecords[i].id)
      }
      const willEmptyPage = selectedRecords.length >= selfGuidedData.length
      const targetPage = willEmptyPage && currentPage === totalPages && currentPage > 1 ? currentPage - 1 : currentPage
      if (targetPage !== currentPage) setCurrentPage(targetPage)
      await getSelfGuided(targetPage, searchTerm)
    } catch (ex) {
      alert(ex)
    } finally {
      setLoading(false)
    }
  }

  const gridRef = useRef(null)

  const rowSelected = () => {
    if (gridRef.current) {
      setSelectedRecords(gridRef.current.getSelectedRecords())
    }
  }

  const rowDeselected = (args) => {
    let tempSelectedRecords = [...selectedRecords]
    for (let i = 0; i < tempSelectedRecords.length; i++) {
      if (args.data.length > 1) {
        for (let j = 0; j < args.data.length; j++) {
          if (tempSelectedRecords[i].id === args.data[j].id) {
            tempSelectedRecords.splice(i, 1)
          }
        }
      } else {
        if (tempSelectedRecords[i].id === args.data.id) {
          tempSelectedRecords.splice(i, 1)
        }
      }
    }
    setSelectedRecords(tempSelectedRecords)
  }

  const onActionComplete = (args) => {
    if (args.requestType === 'columnstate') {
      const gridCols = gridRef.current?.getColumns() ?? []
      const state = gridCols
        .filter((c) => !!c.field)
        .map((c) => ({ field: c.field, visible: c.visible }))
      setGridVisibility(pageKey, state)
    }
  }

  return (
    <div className='m-2 ml-10 mr-10 mt-5 p-2 p-5 bg-white rounded-3xl relative'>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <div className='loader ease-linear rounded-full border-4 border-t-4 border-white h-12 w-12 animate-spin'></div>
        </div>
      )}
      <div className='flex justify-between items-center h-20'>
        <Header category='' title='Self Guided Packages' />
        <div
          className='w-100 border flex justify-end items-center mb-8 createBtn'
          onClick={() => navigate('/createSelfGuided', { state: { from: 'create', data: {} } })}
        >
          <span>Create</span>
          <img src={CreateAddIcon} style={{ width: 15, height: 15 }} alt='' />
        </div>
      </div>
      {isReady && (
        <GridComponent
          dataSource={selfGuidedData}
          editSettings={editOptions}
          toolbar={toolbarOptions}
          allowPaging={false}
          toolbarClick={toolbarClick}
          rowSelected={rowSelected}
          rowDeselected={rowDeselected}
          allowFiltering={false}
          actionBegin={actionBegin}
          actionComplete={onActionComplete}
          showColumnChooser={true}
          ref={(g) => (gridRef.current = g)}
          allowSorting
        >
          <ColumnsDirective>
            {finalColumns.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject services={[Edit, Toolbar, Page, ColumnChooser]} />
        </GridComponent>
      )}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
      />

      {deleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: 400,
              height: 200,
              background: '#fff',
              borderRadius: 10,
              padding: '15px 20px',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#000', marginBottom: 15 }}>
              Delete Alert
            </div>
            <div style={{ fontSize: 18, color: '#000', marginBottom: 10 }}>
              {selectedRecords.length > 0
                ? 'Are you sure you want to delete selected rows.'
                : 'Please select at least one row.'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 20 }}>
              <div
                className='confirmOkBtn'
                onClick={() => {
                  setDeleteConfirm(false)
                }}
              >
                {selectedRecords.length > 0 ? 'Cancel' : 'Ok'}
              </div>
              {selectedRecords.length > 0 && (
                <div className='confirmOkBtn' onClick={handleDelete}>
                  Yes
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SelfGuided
